import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, startWith, switchMap, tap } from 'rxjs/operators';

import { useToaster } from '../../../shared/composables/use-toaster';
import { PAGE_TYPES, PageType } from '../../../shared/pageType';
import { GatewayId } from '../../../site-gateway/models/gateway';
import { SiteGatewayDetailPageActions } from '../../../site-gateway/store/actions/site-gateway-detail-page.actions';
import { getSiteByGatewayId } from '../../../site-list/store/selectors/sites';
import { SiteOwnerDetailPageActions } from '../../../site-owner/store/actions/site-owner-detail-page.actions';
import { isToolbox } from '../../../toolbox/store/selectors/toolbox';
import { SiteDetailPageActions } from '../actions/site-detail-page.actions';
import { SiteHistoryPageActions } from '../actions/site-history-page.actions';
import { getSiteDetailRouteContext, SiteRouteContext } from '../selectors/router';

@Injectable()
export class SiteDetailNavigationEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly toaster = useToaster();

    emitSiteDetailOpenedOnUrlEnter$ = this.onPageEnter(PAGE_TYPES.SITE_DETAIL, (gatewayId) =>
        SiteDetailPageActions.opened({ gatewayId }),
    );

    emitSiteOwnerDetailOpenedOnUrlEnter$ = this.onPageEnter(PAGE_TYPES.SITE_OWNER, (gatewayId) =>
        SiteOwnerDetailPageActions.opened({ gatewayId }),
    );

    emitSiteGatewayDetailOpenedOnUrlEnter$ = this.onPageEnter(PAGE_TYPES.SITE_GATEWAY_DETAIL, (gatewayId) =>
        SiteGatewayDetailPageActions.opened({ gatewayId }),
    );

    emitSiteHistoryOpenedOnUrlEnter$ = this.onPageEnter(PAGE_TYPES.SITE_HISTORY, (gatewayId) =>
        SiteHistoryPageActions.opened({ gatewayId }),
    );

    emitSiteDetailEnteredOnUrlEnter$ = createEffect(() =>
        this.store.select(getSiteDetailRouteContext).pipe(
            startWith(null),
            distinctUntilChanged((previous, current) => previous?.gatewayId === current?.gatewayId),
            pairwise(),
            filter(
                (routes): routes is [SiteRouteContext, NonNullable<SiteRouteContext>] =>
                    !routes[0]?.gatewayId && !!routes[1]?.gatewayId && routes[1].pageType === PAGE_TYPES.SITE_DETAIL,
            ),
            map(([, { gatewayId, deviceId }]) => SiteDetailPageActions.entered({ gatewayId, deviceId })),
        ),
    );

    redirectIfIsToolbox$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteDetailPageActions.opened),
                switchMap(({ gatewayId }) => this.store.select(getSiteByGatewayId(gatewayId)).pipe(first(Boolean))),
                switchMap((site) => this.store.select(isToolbox(site.id)).pipe(first())),
                filter(Boolean),
                tap(() => {
                    this.toaster.error({
                        title: this.translateService.instant('REDIRECTION_TOOL_SITE'),
                        duration: 5000,
                    });
                    void this.router.navigate(['/sites']);
                }),
            ),
        { dispatch: false },
    );

    private onPageEnter(targetPageType: PageType, actionFactory: (id: GatewayId) => Action) {
        return createEffect(() =>
            this.store.select(getSiteDetailRouteContext).pipe(
                distinctUntilChanged(
                    (previous, current) =>
                        previous?.gatewayId === current?.gatewayId && previous?.pageType === current?.pageType,
                ),
                filter(
                    (route): route is NonNullable<SiteRouteContext> =>
                        !!route?.gatewayId && route.pageType === targetPageType,
                ),
                map((route) => actionFactory(route.gatewayId)),
            ),
        );
    }
}
